import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import TracksTable from '../TracksTable';
import LoadMoreBtn from '../LoadMoreBtn';
import Loading from '../Loading';
import ListFilter from '../ListFilter';
import FollowedFilter from '../FollowedFilter';
import { useApi } from '../../context/ApiProvider';
import { Typography, Box } from '@mui/material';

const TracksSection = ({
  dataFrom,
  title,
  hideTableInteraction,
  hideFilter,
  showFavoritesFilter,
}) => {
  const [loading, setLoading] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [paginationConfig, setPaginationConfig] = useState({
    order_by: 'track_meta.release_date',
    order_direction: 'desc',
  });
  const [favoritesFilter, setFavoritesFilter] = useState(false);

  const api = useApi();

  useEffect(() => {
    loadTracks();
  }, [paginationConfig, dataFrom, favoritesFilter]);

  function onFilter(genre, sortBy, sortDirection) {
    let orderBy;
    const isPurchased = dataFrom.includes('/tracks/purchased');

    if (
      isPurchased &&
      (sortBy === 'first_purchased_date' || sortBy === 'purchased_date')
    ) {
      orderBy = 'user_track_favs.first_purchased_date';
    } else {
      orderBy = `track_meta.${sortBy}`;
    }

    setPaginationConfig({
      ...paginationConfig,
      genre: genre,
      order_direction: sortDirection,
      order_by: orderBy,
    });
  }

  const handleFavoritesFilterChange = (event) => {
    const newFavoritesFilter = event.target.checked;
    setFavoritesFilter(newFavoritesFilter);
  };

  async function loadTracks() {
    setLoading(true);
    let endpoint = dataFrom;
    if (favoritesFilter) {
      if (dataFrom.includes('/tracks/purchased')) {
        // Handle purchased tracks: /tracks/purchased -> /tracks/purchased/favs
        endpoint = dataFrom.replace(
          '/tracks/purchased',
          '/tracks/purchased/favs'
        );
      } else {
        // Handle regular tracks: /tracks -> /tracks/favs
        endpoint = dataFrom.replace('/tracks', '/tracks/favs');
      }
    }
    const response = await api.get(endpoint, paginationConfig);
    if (response.ok) {
      const data = response.body.data ? response.body.data : response.body;
      setTracks(data);
      setPagination(response.body.pagination);
      setPage(1);
    } else {
      console.log(response);
    }
    setLoading(false);
  }

  async function loadMore() {
    setLoadingMore(true);
    let endpoint = dataFrom;
    if (favoritesFilter) {
      if (dataFrom.includes('/tracks/purchased')) {
        // Handle purchased tracks: /tracks/purchased -> /tracks/purchased/favs
        endpoint = dataFrom.replace(
          '/tracks/purchased',
          '/tracks/purchased/favs'
        );
      } else {
        // Handle regular tracks: /tracks -> /tracks/favs
        endpoint = dataFrom.replace('/tracks', '/tracks/favs');
      }
    }
    const response = await api.get(endpoint, {
      ...paginationConfig,
      offset: 20 * page,
    });
    if (response.ok) {
      setTracks([...tracks, ...response.body.data]);
      setPagination(response.body.pagination);
      setPage(page + 1);
    } else {
      console.log(response);
    }
    setLoadingMore(false);
  }

  if (tracks.length === 0) return null;

  return (
    <>
      <Typography variant="h5" color={'white'}>
        {title}
      </Typography>
      {loading && (
        <Box textAlign="center">
          <Loading alterColor />
        </Box>
      )}
      <Box mb={6.5} textAlign="center" display={loading ? 'none' : 'block'}>
        <Box mb={2}>
          {!hideFilter && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              {showFavoritesFilter && (
                <FollowedFilter
                  checked={favoritesFilter}
                  onChange={handleFavoritesFilterChange}
                  mode="favorites"
                />
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 2 }}>
                <ListFilter onFilter={onFilter} listType="tracks" />
              </Box>
            </Box>
          )}
          <TracksTable
            items={tracks}
            onTableInteraction={setPaginationConfig}
            tableInteractionConfig={paginationConfig}
            hideTableInteraction={hideTableInteraction}
            showPurchaseDate={dataFrom.includes('/tracks/purchased')}
            showFirstLikedDate={
              dataFrom.includes('/users/') &&
              dataFrom.includes('/tracks') &&
              !dataFrom.includes('/purchased')
            }
          />
        </Box>
        <Box textAlign="center">
          <LoadMoreBtn
            pagination={pagination}
            onLoadMoreClick={loadMore}
            loading={loadingMore}
          />
        </Box>
      </Box>
    </>
  );
};

TracksSection.propTypes = {
  dataFrom: PropTypes.string,
  title: PropTypes.string,
  hideTableInteraction: PropTypes.bool,
  hideFilter: PropTypes.bool,
  showFavoritesFilter: PropTypes.bool,
};

export default TracksSection;
